// src/lib/auth/roles.ts
//
// Single source of truth for staff position → permissions. Previously this
// lived as an inline const inside App.jsx (not reusable anywhere else),
// which is why ProtectedDashboard in index.tsx could only check "is there a
// session" and not "is this staff member allowed into this section" — this
// module is what makes a real section-level route guard possible.
//
// App.jsx should import PERMISSIONS/hasPermission/canViewSection from here
// instead of keeping its own copy, so the two never drift apart.

import type { SupabaseClient } from '@supabase/supabase-js'

export type SectionAccess = 'all' | string[]

export interface PositionPerms {
    sections: SectionAccess
    canEdit: boolean
    canDelete: boolean
    financial: boolean
}

export type PermissionAction = 'all' | 'edit' | 'delete' | 'financial' | 'view'

// Kept byte-for-byte in sync with the object that used to live inline in
// App.jsx (src/App.jsx around line 3794). 'ITSM' has been added to the
// scoped-access positions that should be able to use the new ITSM module;
// positions with sections: 'all' get it automatically since canViewSection
// short-circuits on 'all'. Adjust this list any time — it's just data.
export const PERMISSIONS: Record<string, PositionPerms> = {
    'CEO': { sections: 'all', canEdit: true, canDelete: true, financial: true },
    'Program Manager': { sections: 'all', canEdit: true, canDelete: true, financial: true },
    'Operations Manager': { sections: 'all', canEdit: true, canDelete: true, financial: true },
    // ✅ Coordination Team كانت sections:'all' (Full Access زي CEO) — ده كان
    // إعداد مؤقت قبل ما يتحدد شكل البورتال بتاعهم. دلوقتي عندهم بورتال مخصص
    // (CoordinationSection.tsx) زي HR/Accounts بالظبط، مقفول على صفحة
    // الماتريال بس. canEdit/canDelete هنا false مش لأنهم ملهومش صلاحية تعديل
    // الماتريال — المودال/الحذف/الامبورت جوه MaterialsView.jsx مفعّلين دايمًا
    // بدون أي permission check — ده بس عشان يطلعوهم من قوائم الإدارة العامة
    // (Settings/Switch Project في UserMenu, Client Portal في الهيدر) اللي
    // بتتفعل بناءً على hasPermission('all') = canEdit && canDelete، زي ما هو
    // مطبّق فعلاً مع HR/Accounts.
    'Coordination Team': { sections: ['Coordination'], canEdit: false, canDelete: false, financial: false },
    'Project Manager': {
        sections: ['Overview', 'Project Schedule', 'Tasks', 'Daily Activities', 'Team', 'Materials', 'Photos', 'Invoices', 'Client Account', 'EVM', 'Comments', 'Timeline', 'Documents', 'Expenses Management', 'Location', 'Systems Library', 'Submittal Assembler', 'Approved Brands', 'ITSM'],
        // بدون: HR, Meetings, Prequal Profile, Showcase Tokens, Showcase Leads, Showcase Analytics
        canEdit: true, canDelete: true, financial: true,
    },
    'Site Engineer': {
        sections: ['Overview', 'Project Schedule', 'Tasks', 'Daily Activities', 'Team', 'Materials', 'Photos', 'Comments', 'Timeline', 'Documents', 'Expenses Management', 'Location', 'Systems Library', 'Submittal Assembler', 'ITSM'],
        canEdit: true, canDelete: true, financial: false,
    },
    'Engineer': { sections: 'all', canEdit: true, canDelete: false, financial: false },
    'HR': {
        sections: ['HR'],
        canEdit: false, canDelete: false, financial: false,
    },
    'Accounts': {
        sections: ['Accounts'],
        canEdit: false, canDelete: false, financial: true,
    },
    'Supervisor': {
        sections: ['Overview', 'Project Schedule', 'Tasks', 'Team', 'Materials', 'Photos', 'Comments', 'Timeline', 'Documents', 'Location', 'Daily Activities'],
        canEdit: true, canDelete: true, financial: false,
    },
    'Foreman': {
        sections: ['Overview', 'Project Schedule', 'Tasks', 'Team', 'Materials', 'Photos', 'Comments', 'Timeline', 'Documents', 'Location'],
        canEdit: true, canDelete: false, financial: false,
    },
    'Viewer': {
        sections: ['Overview', 'Tasks', 'Team', 'Materials', 'Photos', 'Timeline', 'Documents', 'Location'],
        canEdit: false, canDelete: false, financial: false,
    },
}

export const DEFAULT_PERMS: PositionPerms = PERMISSIONS['Viewer']

export function getUserPerms(position?: string | null): PositionPerms {
    return (position && PERMISSIONS[position]) || DEFAULT_PERMS
}

export function hasPermission(position: string | null | undefined, action: PermissionAction): boolean {
    if (!position) return false
    const perms = getUserPerms(position)
    if (action === 'all') return perms.canEdit && perms.canDelete
    if (action === 'edit') return perms.canEdit
    if (action === 'delete') return perms.canDelete
    if (action === 'financial') return perms.financial
    if (action === 'view') return true
    return false
}

export function canViewSection(position: string | null | undefined, section: string): boolean {
    if (!position) return false
    const perms = getUserPerms(position)
    if (perms.sections === 'all') return true
    return perms.sections.includes(section)
}

export interface StaffUser {
    id: string
    user_id: string
    username: string
    name: string
    role: string | null
    department: string | null
    position: string | null
    email: string | null
    phone: string | null
    avatar: string | null
    assigned_projects: string[]
    is_active: boolean | null
}

/**
 * Loads the logged-in staff user's user_profiles row, the same way
 * App.jsx's checkUser() already does (session -> user_profiles by user_id,
 * with a fallback link-by-email path for a profile created before the
 * Supabase Auth user existed). Returns null if there's no session, no
 * profile, or the profile is deactivated.
 */
export async function loadCurrentStaffUser(supabase: SupabaseClient): Promise<StaffUser | null> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) return null

    let profile: any = null

    const { data: byUserId } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle()

    if (byUserId) {
        profile = byUserId
    } else {
        // Profile created before the auth user existed (linked by email) —
        // link it now, same fallback App.jsx's checkUser() already does.
        const { data: byEmail } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('email', session.user.email)
            .is('user_id', null)
            .maybeSingle()

        if (byEmail) {
            await supabase.from('user_profiles').update({ user_id: session.user.id }).eq('id', byEmail.id)
            profile = byEmail
        }
    }

    if (!profile) return null
    if (profile.is_active === false) return null

    return {
        id: profile.id,
        user_id: session.user.id,
        username: profile.username,
        name: profile.name,
        role: profile.role ?? null,
        department: profile.department ?? null,
        position: profile.position ?? null,
        email: profile.email || session.user.email || null,
        phone: profile.phone ?? null,
        avatar: profile.avatar ?? null,
        assigned_projects: profile.assigned_projects || [],
        is_active: profile.is_active ?? true,
    }
}
